import { AfterViewInit, Component, inject, ViewChild, OnDestroy, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { NgScrollbar } from 'ngx-scrollbar';
import { viewProjectSelectors } from '../../../store/view-project/view-project.selectors';
import { selectAllLineItems, selectAllSpendSubItems } from '../../../store/spend/spend.selectors';
import { GeneralSidebarHeaderComponent } from '../../general-sidebar-header/general-sidebar-header.component';
import { FloatingTextareaComponent } from '../../inputs/floating-textarea/floating-textarea.component';
import { FloatingInputComponent } from '../../inputs/floating-input/floating-input.component';
import { DropdownComponent } from '../../inputs/dropdown/dropdown.component';
import { SimpleButtonComponent } from '../../buttons/simple-medium-button/simple-button.component';
import { ProjectSpendService } from '../../../services/project-spend.service';
import { InteractionBarStateService } from '../../../services/interaction-bar-state.service';
import { distributionTypes } from '../../constants/spend.constants';
import { InputCalendarComponent } from '../../inputs/input-calendar/input-calendar.component';
import { ProjectDto } from '../../../pages/webapp/projects/projects.interface';
import { INTERACTION_BAR_STATES } from '../../constants/interaction-bar.constants';
import { MANAGE_PROJECT_TABS } from '../../constants/view-project.constants';
import {
  BudgetAdjustmentModel,
  IBudgetAdjustmentPayload,
  Items,
} from './budget-adjustment-sidebar.types';
import { CurrentUserService } from '@app/services/current-user.service';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { NotificationV2Service } from '@app/notification-v2.service';

@Component({
  selector: 'app-budget-adjustment-sidebar',
  imports: [
    DropdownComponent,
    FloatingInputComponent,
    FloatingTextareaComponent,
    ReactiveFormsModule,
    AsyncPipe,
    GeneralSidebarHeaderComponent,
    NgScrollbar,
    SimpleButtonComponent,
    InputCalendarComponent,
    FormsModule,
    MoneyPipe,
  ],
  templateUrl: './budget-adjustment-sidebar.component.html',
  styleUrl: './budget-adjustment-sidebar.component.scss',
})
export class BudgetAdjustmentSidebarComponent implements AfterViewInit, OnDestroy {
  @ViewChild('budgetAdjustmentForm') budgetAdjustmentForm: NgForm;
  protected readonly isLoading = false;
  protected readonly distributionTypes = distributionTypes;

  store = inject(Store);
  notif = inject(NotificationV2Service);
  projectSpendService = inject(ProjectSpendService);
  interactionBar = inject(InteractionBarStateService);
  user = inject(CurrentUserService);

  isNewItemInput = false;

  model: BudgetAdjustmentModel = {
    from_item_id: null,
    to_item_id: null,
    value: null,
    note: null,
    new_item: {
      name: null,
      start_date: null,
      duration: null,
      distribution: null,
    },
  };

  EXTERNAL_ADJUSTMENT = 'External Adjustment';
  ADD_NEW_ITEM_ID = -1;

  toBudgetLineDisabledFields = signal<Set<number>>(new Set<number>());

  project: ProjectDto;
  durationMonths = Array(72)
    .fill(0)
    .map((_, i) => i + 1)
    .map((i) => {
      return {
        label: i === 1 ? `${i} month` : `${i} months`,
        value: i,
      };
    });
  toItems: Items[] = [
    {
      id: this.ADD_NEW_ITEM_ID,
      name: 'Add new Budget Line',
      disabled: false,
      value: null,
      division: null,
      cost_type: null,
    },
  ];
  fromItems: Items[] = [
    {
      id: this.EXTERNAL_ADJUSTMENT,
      name: this.EXTERNAL_ADJUSTMENT,
      value: null,
      division: null,
      cost_type: null,
    },
  ];

  // Select appropriate spend items based on role
  private readonly lineItems$: Observable<{ id: number; name: string; value: number }[]> = this.user
    .isPremiumSP
    ? this.store.select(selectAllSpendSubItems)
    : this.store.select(selectAllLineItems);

  project$ = this.store.select(viewProjectSelectors.getSelectedProject);
  isDestroyed$ = new Subject<boolean>();

  ngAfterViewInit() {
    this.lineItems$
      .pipe(
        filter((lineItems: any[]) => lineItems?.length > 0),
        take(1),
      )
      .subscribe((lineItems: any[]) => {
        this.fromItems = [...this.fromItems, ...lineItems];
        this.toItems = [...this.toItems, ...lineItems];
      });

    this.project$.pipe(takeUntil(this.isDestroyed$)).subscribe((project) => {
      this.project = project;
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  get fromItem() {
    return this.fromItems.find((item) => item.id === this.model.from_item_id);
  }

  fromItemChange(selectedId: number) {
    // do not let the user select the same item in both dropdowns
    if (selectedId === this.model.to_item_id) {
      this.model.to_item_id = null;
    }
    this.toBudgetLineDisabledFields.set(new Set<number>([selectedId]));
  }

  towardsItemChange(selectedId: number) {
    this.isNewItemInput = selectedId === this.ADD_NEW_ITEM_ID;
    if (selectedId !== this.ADD_NEW_ITEM_ID) {
      this.resetNewLineItem();
    }
  }

  save() {
    this.budgetAdjustmentForm.form.markAllAsTouched();

    if (this.budgetAdjustmentForm.form.invalid) {
      this.notif.showError('Please fill all required fields');
      return;
    } else if (this.model.value === 0) {
      this.notif.showError('Adjustment value cannot be 0');
      return;
    } else if (!this.validAdjustmentNumber()) {
      this.notif.showError('Negative numbers are allowed only from External Adjustment!');
      return;
    } else if (this.isAdjustmentLimitExceeded()) {
      this.notif.showError(
        "Adjustment value cannot be greater than the budget line's value that it's taken from!",
      );
      return;
    }

    this.createBudgetAdjustment();
  }

  onBack() {
    this.interactionBar.openInteractionBar(INTERACTION_BAR_STATES.MANAGE_PROJECT, {
      tab: MANAGE_PROJECT_TABS.BUDGET_CASHFLOW,
    });
  }

  private validAdjustmentNumber() {
    if (this.model.from_item_id === this.EXTERNAL_ADJUSTMENT) return true;
    return this.model.value > 0;
  }

  isAdjustmentLimitExceeded(): boolean {
    if (!this.fromItem || this.fromItem.value === null || !this.model.value) {
      return false;
    }
    const adjustmentValue = this.model.value;
    return adjustmentValue > this.fromItem.value;
  }

  private createBudgetAdjustment() {
    this.notif.showLoading();
    const rawData: Partial<IBudgetAdjustmentPayload> = this.transformDataForBackend();
    this.projectSpendService.adjustBudget(rawData).then(
      (data) => {
        this.notif.showSuccess('Budget adjusted!');
        setTimeout(() => {
          this.interactionBar.close();
        }, 500);
      },
      (err) => this.notif.showError(err),
    );
  }

  private transformDataForBackend() {
    const payload: Partial<IBudgetAdjustmentPayload> = {
      ...this.model,
      to_item_id: this.model.to_item_id as number,
      project_id: this.project.id,
      value: Number(this.model.value),
      from_item_id:
        this.model.from_item_id === this.EXTERNAL_ADJUSTMENT
          ? null
          : (this.model.from_item_id as number),
      is_external_adjustment: this.model.from_item_id === this.EXTERNAL_ADJUSTMENT,
    };

    // if external adjustment, from_item_id has to be equal to to_item_id
    if (payload.is_external_adjustment) {
      payload.from_item_id = payload.to_item_id;
    }

    if (this.isNewItemInput) {
      delete payload.to_item_id;
    } else {
      delete payload.new_item;
    }

    if (this.isNewItemInput && payload.is_external_adjustment) {
      delete payload.from_item_id;
    }

    return payload;
  }

  private resetNewLineItem() {
    this.model.new_item = {
      name: null,
      start_date: null,
      duration: null,
      distribution: null,
    };
  }
}
